/**
 * Notifications Panel (SCR-000, SCR-004)
 *
 * @requirement BRD-000 Foundational Screens
 * @screen SCR-000 Application Shell - Notifications panel
 * @screen SCR-004 Notifications Center
 */

import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Icon, type IconName, Spinner } from '@myvault/shared/components';
import { cn } from '@myvault/shared/utils';
import { useNotifications, type NotificationDisplay } from '../../hooks/useNotifications';
import type { NotificationType } from '@myvault/shared/types/notification';

interface NotificationsPanelProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Get icon and color for notification type
 */
function getNotificationStyle(type: NotificationType): { icon: IconName; color: string } {
  switch (type) {
    case 'document_uploaded':
    case 'document_updated':
      return { icon: 'document', color: 'text-blue-600' };
    case 'document_shared':
      return { icon: 'share', color: 'text-green-700' };
    case 'document_expiring':
    case 'document_expired':
    case 'document_deleted':
      return { icon: 'documentText', color: 'text-amber-500' };
    case 'ai_summary_ready':
    case 'ai_comparison_ready':
      return { icon: 'sparkles', color: 'text-amber-500' };
    case 'delegate_invited':
    case 'delegate_accepted':
    case 'delegate_revoked':
    case 'delegate_accessed':
      return { icon: 'userGroup', color: 'text-green-700' };
    case 'emergency_access_triggered':
      return { icon: 'exclamationTriangle', color: 'text-red-600' };
    case 'subscription_renewed':
    case 'subscription_expiring':
    case 'subscription_expired':
    case 'payment_failed':
      return { icon: 'creditCard', color: 'text-blue-600' };
    case 'usage_limit_warning':
      return { icon: 'chartBar', color: 'text-amber-500' };
    case 'security_alert':
      return { icon: 'shieldExclamation', color: 'text-red-600' };
    case 'maintenance_scheduled':
    case 'system_update':
      return { icon: 'cog', color: 'text-gray-600' };
    case 'feature_announcement':
      return { icon: 'megaphone', color: 'text-blue-600' };
    default:
      return { icon: 'bell', color: 'text-gray-600' };
  }
}

export function NotificationsPanel({ open, onClose }: NotificationsPanelProps) {
  const navigate = useNavigate();
  const {
    notifications,
    isLoading,
    unreadCount,
    markAsRead,
    markAllAsRead,
    isMarkingAllRead,
  } = useNotifications(10);

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const handleNotificationClick = (notification: NotificationDisplay) => {
    // Mark as read
    if (!notification.isRead) {
      markAsRead(notification.id);
    }
    // Navigate to action URL if available
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
      onClose();
    }
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 z-50 bg-black/50 transition-opacity',
          open ? 'opacity-100' : 'pointer-events-none opacity-0'
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        className={cn(
          'fixed inset-y-0 right-0 z-50 w-full max-w-sm transform bg-white shadow-xl transition-transform duration-200',
          open ? 'translate-x-0' : 'translate-x-full'
        )}
        role="dialog"
        aria-modal="true"
        aria-label="Notificaties"
      >
        {/* Header */}
        <div className="flex h-16 items-center justify-between border-b border-gray-100 px-4">
          <h2 className="text-lg font-semibold text-gray-900">Notificaties</h2>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button
                className="rounded px-2 py-1 text-sm text-blue-600 hover:bg-blue-50 disabled:opacity-50"
                onClick={handleMarkAllAsRead}
                disabled={isMarkingAllRead}
              >
                {isMarkingAllRead ? 'Bezig...' : 'Alles gelezen'}
              </button>
            )}
            <button
              onClick={onClose}
              className="rounded p-2 text-gray-500 hover:bg-gray-100"
              aria-label="Sluit notificaties"
            >
              <Icon name="close" variant="outline" size="md" />
            </button>
          </div>
        </div>

        {/* Notifications list */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Spinner size="lg" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Icon name="bell" variant="outline" size="xl" className="text-gray-300" />
              <p className="mt-4 font-medium text-gray-900">Geen notificaties</p>
              <p className="mt-1 text-sm text-gray-500">Je bent helemaal bij!</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {notifications.map((notification) => {
                const { icon, color } = getNotificationStyle(notification.type);
                return (
                  <button
                    key={notification.id}
                    className={cn(
                      'flex w-full gap-3 p-4 text-left transition-colors hover:bg-gray-50',
                      !notification.isRead && 'bg-blue-50/50'
                    )}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className={cn('mt-0.5', color)}>
                      <Icon name={icon} variant="solid" size="md" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900">{notification.title}</p>
                      <p className="mt-0.5 text-sm text-gray-600 line-clamp-2">
                        {notification.message}
                      </p>
                      <p className="mt-1 text-xs text-gray-400">{notification.timeAgo}</p>
                    </div>
                    {!notification.isRead && (
                      <div className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-blue-600" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 p-4">
          <Link
            to="/notifications"
            onClick={onClose}
            className="block text-center text-sm font-medium text-blue-600 hover:text-blue-700"
          >
            Alle notificaties bekijken
          </Link>
        </div>
      </div>
    </>
  );
}
